import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setRedemptionCode } from '@/features/checkout/checkoutSlice';
import { redemptionDiscountCents, rewardValueLabel } from '../format';
import { useGetLoyaltyCardQuery } from '../loyaltyApi';

interface Props {
  restaurantUuid: string;
  /** Goods subtotal, in cents: the best coupon is the one worth most on THIS cart. */
  subtotalCents: number;
}

/**
 * SC-1091 M8 — « 🎁 1 avantage : −5 € · Utiliser » in the cart drawer. Using a
 * coupon online took eight taps and the cart never mentioned it. « Utiliser »
 * preselects the coupon for checkout through the checkout slice (in memory — the
 * code never travels in a URL); checkout shows it applied, with its exact discount.
 * Mounted for logged-in customers only, and only when a coupon is usable online.
 */
export default function CartLoyaltyLine({ restaurantUuid, subtotalCents }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const applied = useAppSelector((s) => s.checkout.redemptionCode);
  const { data: card } = useGetLoyaltyCardQuery(restaurantUuid, { skip: !restaurantUuid });

  const usable = (card?.wallet ?? []).filter((c) => c.usable_online);
  if (!card?.enabled || usable.length === 0) return null;

  const best = usable.reduce((top, c) =>
    redemptionDiscountCents({ type: c.reward_type, value: c.value }, subtotalCents) >
    redemptionDiscountCents({ type: top.reward_type, value: top.value }, subtotalCents)
      ? c
      : top,
  );
  const value = rewardValueLabel(t, { type: best.reward_type, value: best.value }) ?? best.reward_name;
  const isApplied = usable.some((c) => c.code === applied);

  return (
    <div
      data-testid="cart-loyalty-line"
      className="flex items-center gap-2 rounded-btn-sm border-2 border-solid border-ink bg-cream px-3 py-2 font-mono text-xs text-ink"
    >
      <span className="min-w-0 flex-1">
        <span aria-hidden="true">🎁 </span>
        {usable.length === 1 ? (
          t('cart.loyalty.one', { value })
        ) : (
          t('cart.loyalty.many', { n: usable.length, value })
        )}
      </span>
      <button
        type="button"
        onClick={() => dispatch(setRedemptionCode(isApplied ? '' : best.code))}
        aria-pressed={isApplied}
        data-testid="cart-loyalty-use"
        className={`min-h-[44px] flex-none rounded-btn-sm border-2 border-solid border-ink px-3 font-display text-sm ${
          isApplied ? 'bg-ink text-paper' : 'bg-mint text-[var(--on-primary)]'
        }`}
      >
        {isApplied ? `✓ ${t('cart.loyalty.applied')}` : t('cart.loyalty.use')}
      </button>
    </div>
  );
}
